import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

let cache = null;
let inflight = null;

/** Fetch the glossary term list once per session. */
export const useGlossaryTerms = () => {
  const [terms, setTerms] = useState(cache || []);

  useEffect(() => {
    if (cache) {
      setTerms(cache);
      return;
    }
    if (!inflight) {
      inflight = axios
        .get(`${API}/glossary-terms`)
        .then((r) => {
          cache = r.data?.items || [];
          return cache;
        })
        .catch(() => {
          cache = [];
          return cache;
        });
    }
    let alive = true;
    inflight.then((items) => alive && setTerms(items));
    return () => {
      alive = false;
    };
  }, []);

  return terms;
};

const escapeRx = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * Build a matcher: longest names first so "corn syrup solids" wins over "corn syrup".
 * Only the first occurrence of each ingredient in a page gets linked.
 */
export const buildMatcher = (terms) => {
  const entries = [];
  (terms || []).forEach((t) => {
    const labels = [t.name, ...(t.aliases || [])].filter(
      (l) => typeof l === "string" && l.trim().length > 3
    );
    labels.forEach((l) =>
      entries.push({ label: l.replace(/\s*\(.*?\)\s*/g, " ").trim(), slug: t.slug })
    );
  });
  entries.sort((a, b) => b.label.length - a.label.length);
  const seen = new Set();
  const unique = entries.filter((e) => {
    const k = e.label.toLowerCase();
    if (!k || seen.has(k)) return false;
    seen.add(k);
    return true;
  });
  if (!unique.length) return null;
  const rx = new RegExp(
    `\\b(${unique.map((u) => escapeRx(u.label)).join("|")})\\b`,
    "gi"
  );
  const bySlug = new Map(unique.map((u) => [u.label.toLowerCase(), u.slug]));
  return { rx, bySlug };
};

/**
 * Linkify a plain-text string using a shared `used` Set.
 * NOTE: prefer `linkifySections` — mutating a Set during render is not safe.
 */
const Linkify = ({ text, matcher, used, max = 8 }) => {
  if (!text) return null;
  if (!matcher || !used || used.size >= max) return <>{text}</>;
  return <>{linkifyText(text, matcher, used, max)}</>;
};

/** Pure helper: returns an array of strings and <Link> nodes. */
export const linkifyText = (text, matcher, used, max = 8) => {
  if (!text || !matcher) return [text];
  const nodes = [];
  let last = 0;
  const rx = new RegExp(matcher.rx.source, "gi");
  let m;
  while ((m = rx.exec(text)) !== null) {
    if (!m[0]) break;
    const slug = matcher.bySlug.get(m[0].toLowerCase());
    if (!slug || used.has(slug) || used.size >= max) continue;
    used.add(slug);
    if (m.index > last) nodes.push(text.slice(last, m.index));
    nodes.push(
      <Link className="ix-link" to={`/ingredients/${slug}`} key={`${slug}-${m.index}`}>
        {m[0]}
      </Link>
    );
    last = m.index + m[0].length;
  }
  if (!nodes.length) return [text];
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
};

/**
 * Deterministically linkify a whole article. Call inside useMemo so the work
 * (and the "already linked" bookkeeping) happens once per post, not per render.
 */
export const linkifySections = (sections, matcher, max = 8) => {
  const used = new Set();
  return (sections || []).map((s) => ({
    ...s,
    paragraphNodes: (s.paragraphs || []).map((p) =>
      matcher ? linkifyText(p, matcher, used, max) : [p]
    ),
    bulletNodes: (s.bullets || []).map((b) =>
      matcher ? linkifyText(b, matcher, used, max) : [b]
    ),
  }));
};

export default Linkify;
